import { StateService } from '@uirouter/angular';

/**
 *  Directive that provides a search/filters panel functionality
 *
 *  The directive takes two parameters:
 *
 *  * title
 *  * filters
 *  * sortFields
 *
 *  filter object format:
 *
 *  {
 *    "section-name": [
 *      {
 *        "alias": "some-field"
 *      }
 *    ]
 *  }
 *
 *  Notice: the accordion titles will be converted from the section property name, e.g.:
 *     .property name:  "section-name"
 *     .accordion title:  "Section Name"
 *
 *  filters example:
 *
 *    {
 *      "section-1-title": [
 *          {
 *             "alias": "summary",
 *             "fieldName": "Summary",
 *             "value": "Next",
 *             "fieldType": 4,
 *             "$$hashKey": "object:825",
 *             "isExtraField": false
 *          }
 *      ]
 *     },
 *     "section-2-title": [
 *         {
 *           "alias": "assignees",
 *           "fieldName": "Assignee",
 *           "value": {
 *               "id": 0,
 *               "value": "",
 *               "label": "Any"
 *           },
 *           "fieldType": 1,
 *           "fieldElements": [
 *               {
 *                  "id": 0,
 *                  "value": "",
 *                  "label": "Any"
 *               },
 *               {
 *                  "id": 100,
 *                  "value": "100",
 *                  "label": "None"
 *               },
 *               {
 *                  "id": 33887,
 *                  "value": 33887,
 *                  "label": "Celina Vizcaya"
 *               },
 *               {
 *                  "id": 14030,
 *                  "value": 14030,
 *                  "label": "Javier Adrián Ortiz de la Tabla"
 *               },
 *               {
 *                  "id": 8388,
 *                  "value": 8388,
 *                  "label": "Mario Papayannis"
 *               },
 *               {
 *                  "id": 5945,
 *                  "value": 5945,
 *                  "label": "Michael Tutty"
 *               },
 *               {
 *                  "id": 25868,
 *                  "value": 25868,
 *                "label": "Tony Bibbs"
 *            }
 *          ],
 *          "isExtraField": false
 *        }
 *    ]
 *  }
 *
 *  sortFields example:
 *
 *  {
 *   "closeDate": "Close Date",
 *   "id": "ID",
 *   "lastModifiedDate": "Last Modified",
 *   "openDate": "Open Date",
 *   "priority": "Priority",
 *   "submittedByName": "Submitter",
 *   "summary": "Summary",
 *   "tracker_id": "Tracker"
 *  }
 *
 *  How to implement:
 *
 *  a) Add it to your html:
 *
 *  <dynamic-filters title="Filter Criteria" 
 *      on-apply="$ctrl.onApplyFilters" 
 *      on-reset="$ctrl.onResetFilters"
 *      on-sort="$ctrl.onSort"
 *      filters="$ctrl.filters" 
 *      sort-fields="$ctrl.sortFields"></dynamic-filters>
 *
 *  b) Implement the following callbacks in your component:
 *
 *    * onApplyFilters: signals when the user hits the "apply" button
 *
 *    * onResetFilters: signals when the user hits the "reset" button
 *
 *    * onSort: sends a "sort" request. The palyload contains the
 *      following params:
 *        . key: the field key to use for sorting
 *        . sortDir: it's either 'asc' or 'desc'
 *
 */
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

import * as _ from 'lodash';
import { marker } from '../../../core/services/ngx-translate-extract-marker';

// Section titles rendered by renderTitle() from the filter group keys; listed so i18n:extract picks them up
marker('Main Fields');
marker('Extra Fields');

@Component({
  selector: 'dynamic-filters',
  template: require('./dynamic-filters.component.html')
})
export class DynamicFiltersComponent implements OnInit {
  @Input() title: string;
  @Input() filters: any;
  @Output() onApply: EventEmitter<void> = new EventEmitter<void>();
  @Output() onReset: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSort: EventEmitter<{ key: string; sortDir: string }> = new EventEmitter<{ key: string; sortDir: string }>();
  @Input() sortFields: any;

  sortString: string;
  queryParams: any;
  mappedFilters: any;
  mappedSortFields: any;

  constructor(private stateService: StateService) {}

  ngOnInit(): void {
    this.init();

    // Get query params from UI-Router state
    this.queryParams = this.stateService.params;
    this.sortString = this.getSortString();

    // Note: State change listening removed to avoid API issues
    // Parameters will be updated when component reinitializes on route changes
  }

  ngOnChanges(): void {
    this.init();
  }

  init() {
    this.mappedFilters = _.map(this.filters, (value, key) => {
      return { section: key, filters: value };
    });

    this.mappedSortFields = _.map(this.sortFields, (value, key) => {
      return { id: key, label: value };
    });
  }

  getSortString(field?: string, dir?: string): string {
    if (!field || !dir) {
      const curQS = { ...this.queryParams, ...this.stateService.params };
      field = _.get(curQS, 'sort_field', 'id');
      dir = _.get(curQS, 'sort_dir', 'asc');
    }
    return `${_.get(this.sortFields, field, field)} ${dir.toUpperCase()}`;
  }

  renderTitle(str: string): string {
    return _.split(str, '_').map(_.capitalize).join(' ');
  }

  applyFilters(): void {
    this.onApply.emit();
  }

  resetFilters(): void {
    this.onReset.emit();
  }

  setSort(event: any, field: string, dir: string): void {
    event.preventDefault();
    event.stopPropagation();

    this.sortString = this.getSortString(field, dir);
    const newSort = { key: field, sortDir: dir };
    this.onSort.emit(newSort);
  }

  isFilterEnabled(filter: any, fieldType: number): boolean {
    if (!filter.hasOwnProperty('isExtraField')) {
      filter.isExtraField = false;
    }

    filter.isExtraField = (filter.isExtraField === 'true' || filter.isExtraField) && filter.fieldType !== 7;

    return filter.fieldType === fieldType;
  }

  isSortedBy(field: string, dir: string): boolean {
    return this.sortString === this.getSortString(field, dir);
  }

  resetFilterField(event: any, filter: any): void {
    event.preventDefault();
    event.stopPropagation();

    if ([4, 6, 9, 10, 11].includes(parseInt(filter.fieldType))) {
      filter.value = '';
    } else {
      filter.value = {
        id: '',
        value: 'any',
        label: 'Any'
      };
    }
  }

  showPlainFilterBadge(filter: any): boolean {
    return [4, 6, 9, 10, 11].includes(filter.fieldType) && filter.value !== '';
  }

  showSelectFilterBadge(filter: any): boolean {
    return [1, 2, 3, 5, 7, 8, 12].includes(parseInt(filter.fieldType)) && !_.isEmpty(filter.value) &&
      filter.value.id !== '' && filter.value.hasOwnProperty('label');
  }

  toggleFilterPanel(item: string | number): void {
    const elem: any = $(document.getElementById(String(item)));
    elem.collapse('toggle');
  }

}
